/**
 * People → Talent Acquisition → "Nueva búsqueda": el Hiring Manager abre una
 * búsqueda (el agente arranca con la pregunta de apertura, spec §3), la retoma
 * si quedó a medias (ABANDONED se conserva como borrador, spec §2) y ve las que
 * ya guardó. Lista a la izquierda, chat a la derecha; en mobile, apilado.
 * La sesión elegida va por query param (?intake=) para poder linkearla.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { normalizeLang } from '@/../../shared/lang';
import { MessageSquarePlus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useStartTaIntake, useTaIntakeMine } from '@/hooks/useTalentAcquisition';
import { cn, formatDate } from '@/lib/utils';
import { estaGuardada } from '@/../../shared/ta-intake';
import { TaIntakeChat } from './TaIntakeChat';
import { TaIntakeStatusBadge } from './TaIntakeStatusBadge';

export function TaIntakeTab() {
  const { t, i18n } = useTranslation();
  const k = 'talent_acquisition.intake';
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = searchParams.get('intake');
  const mine = useTaIntakeMine();
  const start = useStartTaIntake();

  const select = (sessionId: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (sessionId) params.set('intake', sessionId); else params.delete('intake');
    setSearchParams(params, { replace: true });
  };

  // Sin selección: se abre la más reciente en curso (si hay)
  useEffect(() => {
    if (selected || !mine.data?.length) return;
    const enCurso = mine.data.find((s) => !estaGuardada(s.status));
    if (enCurso) select(enCurso.sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine.data, selected]);

  const nueva = () => start.mutate({ lang: normalizeLang(i18n.language) }, { onSuccess: (view) => select(view.session.sessionId) });
  const sesiones = mine.data ?? [];
  const enCurso = sesiones.filter((s) => !estaGuardada(s.status));
  const guardadas = sesiones.filter((s) => estaGuardada(s.status));

  const Item = ({ s }: { s: (typeof sesiones)[number] }) => (
    <button
      type="button" onClick={() => select(s.sessionId)}
      className={cn('w-full rounded-lg border px-3 py-2 text-left transition-colors hover:bg-muted', selected === s.sessionId && 'border-primary bg-muted')}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium">{s.jobTitle || t(`${k}.chat.untitled`)}</span>
        <TaIntakeStatusBadge status={s.status} className="text-[10px]" />
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">{s.areaTeam || '—'} · {formatDate(s.updatedAt)}</p>
    </button>
  );

  return (
    <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
      <aside className="space-y-4">
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-brand-green" />
            <div className="min-w-0">
              <h2 className="font-semibold">{t(`${k}.title`)}</h2>
              <p className="mt-1 text-xs text-muted-foreground">{t(`${k}.desc`)}</p>
            </div>
          </div>
          <Button className="mt-3 w-full gap-1.5" onClick={nueva} disabled={start.isPending}>
            {start.isPending ? <WinclapLoader size={14} /> : <MessageSquarePlus className="h-4 w-4" />}{t(`${k}.new`)}
          </Button>
        </div>
        {mine.isLoading && <div className="flex justify-center py-6"><WinclapLoader size={24} /></div>}
        {enCurso.length > 0 && (
          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t(`${k}.mine_in_progress`)}</p>
            {enCurso.map((s) => <Item key={s.sessionId} s={s} />)}
          </div>
        )}
        {guardadas.length > 0 && (
          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t(`${k}.mine_saved`)}</p>
            {guardadas.map((s) => <Item key={s.sessionId} s={s} />)}
          </div>
        )}
      </aside>
      <section className="min-w-0">
        {selected ? (
          <TaIntakeChat sessionId={selected} onArchived={() => select(null)} />
        ) : (
          <div className="flex min-h-[40vh] flex-col items-center justify-center rounded-xl border border-dashed p-8 text-center">
            <Sparkles className="h-8 w-8 text-muted-foreground/60" />
            <p className="mt-3 max-w-md text-sm text-muted-foreground">{t(`${k}.empty`)}</p>
            <Button className="mt-4 gap-1.5" onClick={nueva} disabled={start.isPending}>
              {start.isPending ? <WinclapLoader size={14} /> : <MessageSquarePlus className="h-4 w-4" />}{t(`${k}.new`)}
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
